import {useContext, useEffect, useState} from "react";
import './CategoryQuiz.scss'
import {ProgressBar} from "react-bootstrap";
import API, {HandleError} from "../API";
import AppContext from "../AppContext";
import {useSnackbar} from "notistack";
import {User} from "../models/User";
import TopicFinished from "./TopicFinished";
import {useTranslation} from "react-i18next";
import SingleMultiQuestion from "./questions/SingleMultiQuestion";
import ManualQuestion from "./questions/ManualQuestion";
import PuzzleQuestion from "./questions/PuzzleQuestion";

export default function CategoryQuiz({ currentCat, setCurrentCat, course, setCourse, isPractice }) {
    const app = useContext(AppContext)
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar()
    const questions = !isPractice && course.topic ? course.topic.questions : currentCat.questions;
    const [quiz, setQuiz] = useState({
        loading: false,
        answers: [],
        results: questions.map((q, idx) => { return -1 }),
        perfect: true,
        finished: false,
        submitted: false,
        mistakes: [],
        questions: [...questions],
        question: questions[0],
        questionIdx: 0,
        lastResult: -1,
        points: 0,
        token: null
    })
    const hasMistakes = questions.length <= quiz.questionIdx

    useEffect(() => {
        scrollToBottom()

    }, [quiz.lastResult])


    useEffect(() => {
        if(quiz.finished)
            postResult()

    }, [quiz.finished])

    useEffect(() => {
        getToken()
    }, [])

    const getToken = async () => {
        app.startLoading()
        try {
            const res = await API({
                method: 'GET',
                url: '/points-token/' + (isPractice || currentCat?.topics_completed ? currentCat.ID : course.topic.ID),
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                }
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            app.stopLoading()
            setQuiz({...quiz, token: res.data.token})
        }
        catch(e) {
            setQuiz({...quiz, finished: false})
            app.stopLoading()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }
    const postResult = async () => {
        app.startLoading()
        try {
            const res = await API({
                method: 'POST',
                url: '/points',
                headers: {
                    'Authorization': `Bearer ${app.token}`,
                },
                data: {
                    token: quiz.token,
                    event: isPractice ? 'practice_completed' : (currentCat.topics_completed ? 'category_completed' : 'topic_completed'),
                    perfect: quiz.perfect ? 1 : 0,
                    ref_id: isPractice || currentCat.topics_completed ? currentCat.ID : course.topic.ID,
                    parent_id: app.user?.current_course
                }
            })

            if (res.status < 200 || res.status > 299)
                throw res.data;

            app.stopLoading()
            setQuiz({...quiz, points: res.data.points, submitted: true})
            app.setUser(new User(res.data.me));
        }
        catch(e) {
            setQuiz({...quiz, finished: false})
            app.stopLoading()
            HandleError(e, 'Error', enqueueSnackbar, app.logout);
        }
    }

    const percent = () => {
        if(hasMistakes)
            return Math.round((quiz.questionIdx + 1) / quiz.questions.length * 100)
        else
            return Math.round((quiz.questionIdx + 1) / questions.length * 100)
    }

    const goNext = () => {
        const nextIdx = quiz.questionIdx + 1
        if(nextIdx >= quiz.questions.length) {
            setQuiz({...quiz, finished: true })
            return
        }
        const next =  quiz.questions[nextIdx]
        setQuiz({...quiz, lastResult: -1, question: next, questionIdx: nextIdx, answers: [] })
    }

    const closeQuiz = () => {
        setCurrentCat(null)
    }

    const scrollToBottom= () => {
        const ele = document.querySelector('#category-window .category-window-inner');
        if(ele)
            ele.scrollTo({top: 10000, behavior: 'smooth'});
    };

    return <div id="category-quiz" className={quiz.submitted ? 'finished ' : ''}>

        {isPractice || currentCat.topics_completed ?
            <TopicFinished perfect={quiz.perfect} title={quiz.perfect ? t('finished.chapterTitlePerfect') : t('finished.chapterTitle')}
                           message={quiz.perfect ? <div className="text-muted mb-4"><strong>{t('finished.chapterText1Perfect')}</strong><br />
                                   {t('finished.chapterText2Perfect')}
                           </div> : <div className="text-muted mb-4"><strong>{t('nb')}:</strong> {t('finished.chapterText')}
                           </div>} points={quiz.points} goNext={closeQuiz} buttonText={t('button.continue')} />
            :

            <TopicFinished perfect={quiz.perfect} title={quiz.perfect ? t('finished.quizTitlePerfect') : t('finished.quizTitle')}
                           message={quiz.perfect ? <div className="text-muted mb-4"><strong>{t('finished.chapterText1Perfect')}</strong><br />
                               {t('finished.chapterText2Perfect')}
                           </div> : <div className="text-muted mb-4"><strong>{t('nb')}:</strong> {t('finished.chapterText')}
                           </div>} points={quiz.points} goNext={closeQuiz} buttonText={t('button.continue')} />}

        <div className="quiz-running">
            <h4 className="q-title" dangerouslySetInnerHTML={{__html: quiz.question.question }} />
            <div className={"quiz-progress " + (hasMistakes ? 'has-mistakes' : '')}>
                <ProgressBar now={percent()} animated={true} variant={hasMistakes ? 'warning' : 'info'} />
                <div className="quiz-progress-label">
                    {!hasMistakes ? t('quiz.questionCount', { completed: quiz.questionIdx + 1, count: questions.length}) : t('quiz.hasMistakes')}
                </div>
            </div>
            {!quiz.question?.type && <SingleMultiQuestion quiz={quiz} setQuiz={setQuiz} goNext={goNext} />}
            {['single', 'multiple'].indexOf(quiz.question?.type) !== -1 && <SingleMultiQuestion quiz={quiz} setQuiz={setQuiz} goNext={goNext} />}
            {quiz.question?.type === 'manual' && <ManualQuestion quiz={quiz} setQuiz={setQuiz} goNext={goNext} />}
            {quiz.question?.type === 'puzzle' && <PuzzleQuestion quiz={quiz} setQuiz={setQuiz} goNext={goNext} />}
        </div>
    </div>
}